<template>
  <div class="container billing-view">
    <BLoading :active.sync="billingHistory.loadingStatus.isLoading" />

    <div class="columns">
      <div class="column"><h1>Billing History</h1></div>
    </div>
    <div v-if="!billingHistory.loadingStatus.isLoading" class="columns">
      <div class="column">
        <div v-if="!billingHistory.items.length"><p>You have no previous billing history.</p></div>
        <BTable
          v-else
          ref="table"
          detailed
          paginated
          backend-pagination
          :data="allRecords"
          :current-page="billingHistory.pagination.currentPage"
          :per-page="billingHistory.pagination.itemsPerPage"
          :total="billingHistory.pagination.totalItems"
          :loading="billingHistory.loadingStatus.isUpdating"
          @page-change="updateItems"
        >
          <template slot-scope="props" class="spacing">
            <BTableColumn field="timestamp" label="Date" @click.native="toggle(props.row)">
              {{ props.row.timestamp | date('compact') }}
            </BTableColumn>
            <BTableColumn field="note" label="Detail" @click.native="toggle(props.row)">
              <strong>{{ props.row.note }}</strong>
            </BTableColumn>
            <BTableColumn
              field="total"
              label="Total"
              class="row-total"
              @click.native="toggle(props.row)"
            >
              <strong :class="props.row.total > 0 ? 'positive-total' : ''">{{
                props.row.total | currency
              }}</strong>
            </BTableColumn>
          </template>
          <template slot="detail" slot-scope="props">
            <div class="billing-item">
              <div v-for="item in props.row.items" :key="item.id" class="subitem">
                <span
                  ><i>{{ item.note }}</i></span
                >
                <span
                  class="billing-view--item-total"
                  :class="props.row.total > 0 ? 'positive-total' : ''"
                >
                  {{ item.amount | currency }}
                </span>
              </div>
              <div class="subitem-total">
                <span class="subitem-total-header">Total:</span>
                <span
                  class="subitem-total-amount"
                  :class="props.row.total > 0 ? 'positive-total' : ''"
                >
                  {{ props.row.total | currency }}
                </span>
              </div>
            </div>
          </template>
        </BTable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { BILLING_HISTORY__GET_ITEMS, BILLING_HISTORY__UPDATE_ITEMS } from '@/store/types';
import _ from 'lodash';

export default {
  name: 'BillingView',
  computed: {
    ...mapState(['billingHistory']),
    allRecords() {
      const allRecords = _.cloneDeep(this.billingHistory.items);

      _.forEach(allRecords, (singleRecord) => {
        if (singleRecord.tax !== 0) {
          singleRecord.items.push({
            id: 'tax',
            note: 'Tax',
            amount: singleRecord.tax,
          });
        }
      });

      return allRecords;
    },
  },
  created() {
    this.getItems();
  },
  methods: {
    ...mapActions([BILLING_HISTORY__GET_ITEMS, BILLING_HISTORY__UPDATE_ITEMS]),
    getItems() {
      this[BILLING_HISTORY__GET_ITEMS]();
    },
    updateItems(page) {
      this[BILLING_HISTORY__UPDATE_ITEMS]({ page });
    },
    toggle(row) {
      this.$refs.table.toggleDetails(row);
    },
  },
};
</script>

<style lang="scss">
.billing-view {
  h1 {
    margin-bottom: 20px;
  }
  td {
    cursor: pointer;
    font-size: $size-5;
  }

  .spacing {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .billing-item {
    background-color: $primary-light;
  }
  .row {
    padding: 0 10px;
  }
  .row-total {
    text-align: right;
  }
  .positive-total {
    color: $primary;
  }
  .subitem {
    padding-top: 25px;
    padding-bottom: 25px;
    border-bottom: 1px solid $gray-tertiary;
    display: flex;
    justify-content: space-between;
    margin: 0 40px;
  }
  .subitem-total {
    padding-top: 25px;
    padding-bottom: 25px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin: 0 40px;
  }
  .subitem-total-header {
    flex-shrink: 1;
    flex-basis: 100%;
    text-align: right;
  }
  .subitem-total-amount {
    flex-shrink: 0;
    margin-left: 10px;
  }

  .chevron-cell {
    .mdi-chevron-right::after {
      content: url(chevron.svg);
      position: relative;
      top: 3px;
      left: 1px;
    }
  }

  .pagination-next {
    .mdi-chevron-right::after {
      content: url(chevron.svg);
      position: relative;
      top: 3px;
      left: 1px;
    }
  }

  .pagination-previous {
    .mdi-chevron-left::after {
      content: url(chevron.svg);
      position: relative;
      display: inline-block;
      bottom: 3px;
      right: 1px;
      transform: rotate(180deg);
    }
  }
}

@media (max-width: $mobile-max-width) {
  .billing-view {
    margin: 0 auto;

    h1 {
      margin-bottom: 0;
    }
    .billing-item,
    .subitem,
    .subitem-total {
      padding: 10px;
      margin: 0;
      word-break: break-word;
      flex-wrap: wrap;
      .billing-view--item-total {
        margin-left: auto;
      }
    }
    .column {
      padding: 10px 20px;
      margin: 0 auto;
    }
    .columns {
      margin: 0 auto;
    }
  }
}

.themed-app {
  .billing-view {
    td {
      font-size: $theme-txt-body;
    }

    .billing-item {
      background-color: $theme-color-primary-light;
    }

    .positive-total {
      color: $theme-color-primary;
    }

    .subitem {
      border-bottom-color: $theme-color-gray-dark;
    }

    .chevron-cell {
      .icon {
        position: relative;
        top: -4px;
      }

      .mdi-chevron-right::after {
        background-color: $theme-color-primary;
        content: '';
        height: 20px;
        mask: url(chevron.svg);
        position: absolute;
        top: -3px;
        left: -3px;
        width: 20px;
      }
    }

    .pagination-next,
    .pagination-previous {
      .mdi-chevron-left::after,
      .mdi-chevron-right::after {
        background-color: $theme-color-primary;
        content: '';
        height: 20px;
        mask: url(chevron.svg);
        position: absolute;
        top: 10px;
        left: 10px;
        width: 20px;
      }
    }
  }
}
</style>
